import type {
  SiteIoConfiguration,
  SiteReadable,
} from "../../../services/client/types.gen";
import { isReady } from "../../SiteDetails/ValidationTab/utils";
import { phaseRotationDeclarations } from "../../../util/phaseRotations";

export type SitesView = "validation" | "phase_rotation";

export type PhasePermutationMode = NonNullable<
  SiteIoConfiguration["phase_permutation_mode"]
>;

export type BoardSite = {
  site: SiteReadable;
  phasePermutationMode: PhasePermutationMode;
};

export type BoardColumn = {
  key: string;
  title: string;
  hint: string;
  sites: BoardSite[];
};

const sortBySiteName = (sites: BoardSite[]) =>
  sites.sort((left, right) => left.site.name.localeCompare(right.site.name));

export const validationBoardColumns = (sites: BoardSite[]): BoardColumn[] => {
  const columns: BoardColumn[] = [
    {
      key: "invalid",
      title: "Invalid",
      hint: "Validation status ERROR, or the site was never validated.",
      sites: sites.filter(
        ({ site }) => !isReady(site.validation_status?.status),
      ),
    },
    {
      key: "valid",
      title: "Valid",
      hint: "Validation status OK, WARNING or UNKNOWN.",
      sites: sites.filter(({ site }) =>
        isReady(site.validation_status?.status),
      ),
    },
  ];
  columns.forEach((column) => sortBySiteName(column.sites));
  return columns;
};

const allRotationsDeclared = (site: SiteReadable) => {
  const configuration = site.configurations?.site_configuration;
  return (
    Boolean(configuration) &&
    phaseRotationDeclarations(
      configuration,
      site.configurations?.io_configuration,
    ).undeclared.length === 0
  );
};

export const phaseRotationBoardColumns = (
  sites: BoardSite[],
): BoardColumn[] => {
  const columns: BoardColumn[] = [
    {
      key: "use_topology_undeclared",
      title: "Topology, rotations missing",
      hint: "phase_permutation_mode is use_topology, but the site has no configuration or at least one asset declares no phase rotation, so the controller silently falls back to worst-case phase behaviour.",
      sites: sites.filter(
        ({ site, phasePermutationMode }) =>
          phasePermutationMode === "use_topology" &&
          !allRotationsDeclared(site),
      ),
    },
    {
      key: "use_topology_declared",
      title: "Topology, rotations declared",
      hint: "phase_permutation_mode is use_topology and every asset declares a phase rotation — the controller runs phase-aware.",
      sites: sites.filter(
        ({ site, phasePermutationMode }) =>
          phasePermutationMode === "use_topology" && allRotationsDeclared(site),
      ),
    },
    {
      key: "worst_case_declared",
      title: "Worst case, rotations declared",
      hint: "Every asset declares a phase rotation, but the IO configuration still runs worst_case — use_topology could be enabled.",
      sites: sites.filter(
        ({ site, phasePermutationMode }) =>
          phasePermutationMode === "worst_case" && allRotationsDeclared(site),
      ),
    },
    {
      key: "worst_case_undeclared",
      title: "Worst case, rotations missing",
      hint: "phase_permutation_mode is worst_case (or no IO configuration exists) and the site has no configuration or not every asset declares a phase rotation.",
      sites: sites.filter(
        ({ site, phasePermutationMode }) =>
          phasePermutationMode === "worst_case" && !allRotationsDeclared(site),
      ),
    },
  ];
  columns.forEach((column) => sortBySiteName(column.sites));
  return columns;
};
